// Inject one or more JSON-LD structured-data blocks into <head>.
// All script nodes are removed on unmount / when `blocks` changes so
// route changes don't leak stale schema between pages.
//
// Usage:
//   useJsonLd([
//     { '@context': 'https://schema.org', '@type': 'Service', ... },
//     { '@context': 'https://schema.org', '@type': 'BreadcrumbList', ... },
//   ]);
import { useEffect } from 'react';

export function useJsonLd(blocks) {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    // Remove any static JSON-LD blocks that the build-time snapshotter
    // pre-injected — the hydrated copies below become the source of truth so
    // client-side navigation always emits the correct schema for the current route.
    document.head.querySelectorAll('script[data-static-jsonld]').forEach((el) => el.remove());
    if (!Array.isArray(blocks) || blocks.length === 0) return undefined;
    const nodes = blocks.map((data) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-emg-ld', '1'); // marker for future audits
      try { el.textContent = JSON.stringify(data); } catch { el.textContent = '{}'; }
      document.head.appendChild(el);
      return el;
    });
    return () => { nodes.forEach((n) => n.parentNode && n.parentNode.removeChild(n)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(blocks)]);
}

export default useJsonLd;
